import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../services/axios';
import alertService from '../services/alerts';
import '../styles/EditarCursoPage.css';

function CourseForm() {
  const emptyData = { id_curso: '', nombre_curso: '', codigo_curso: '' };
  const [formData, setFormData] = useState(emptyData);
  const [initialData, setInitialData] = useState(emptyData);
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [isFormChanged, setIsFormChanged] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const location = useLocation(); 
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1025);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1025;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    if (window.innerWidth >= 1025) {
      setSidebarOpen(true);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (isEditing) {
      const fetchCourse = async () => {
        try {
          const response = await api.get(`/cursos/${id}`);
          const data = {
            id_curso: response.data.id_curso,
            nombre_curso: response.data.nombre_curso || '',
            codigo_curso: response.data.codigo_curso || ''
          };
          setFormData(data);
          setInitialData(data);
        } catch (err) {
          setError('Error al obtener los datos del curso');
        }
      };
      fetchCourse();
    } else {
      const fetchNextCode = async () => {
        try {
          const response = await api.get('/cursos/next');
          
          const data = {
            id_curso: response.data.next_id,     
            nombre_curso: '',
            codigo_curso: response.data.next_code 
          };
          setFormData(data);
          setInitialData(data);
        } catch (err) {
          setError('Error al obtener el siguiente código');
        }
      };
      fetchNextCode();
    }
  }, [isEditing, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'nombre_curso') {
      setFormData(prev => {
        const updatedData = { ...prev, [name]: value };
        const hasChanges = JSON.stringify(updatedData) !== JSON.stringify(initialData);
        setIsFormChanged(hasChanges);
        return updatedData;
      });
    }
  };

  const hasChanges = useCallback(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  }, [formData, initialData]);

  const validateForm = () => {
    const errors = {};
    if (!formData.nombre_curso.trim()) {
      errors.nombre_curso = 'El nombre del curso es requerido';
    } else if (formData.nombre_curso.length > 255) {
      errors.nombre_curso = 'El nombre no debe exceder 255 caracteres';
    } else if (formData.nombre_curso.trim().length < 2) {
      errors.nombre_curso = 'El nombre del curso debe tener al menos 2 caracteres';
    }
    return errors;
  };

  const handleBackButtonClick = async () => {
    if (hasChanges()) {
      const confirmed = await alertService.confirm(
        'Cambios Sin Guardar',
        'Tienes cambios sin guardar. ¿Seguro que deseas regresar?',
        {
          confirmButtonText: 'Sí, Regresar',
          cancelButtonText: 'Cancelar'
        }
      );
      if (!confirmed.isConfirmed) return;
    }
    navigate('/cursos');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    const confirmed = await alertService.confirm(
      isEditing ? 'Guardar Cambios' : 'Crear Curso',
      isEditing
        ? '¿Estás seguro de guardar los cambios?'
        : '¿Estás seguro de crear este curso?',
      {
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar'
      }
    );
    if (!confirmed.isConfirmed) return;

    try {
      if (isEditing) {
        await api.put(`/cursos/${id}`, {
          nombre_curso: formData.nombre_curso
        });
        await alertService.success(
          'Curso Actualizado',
          'Los cambios han sido guardados exitosamente.'
        );
      } else {
        await api.post('/cursos', {
          nombre_curso: formData.nombre_curso
        });
        await alertService.success(
          'Curso Creado',
          'El curso ha sido creado exitosamente.'
        );
      }
      navigate('/cursos');
    } catch (err) {
      let errorMessage = 'Error al guardar el curso';
      
      if (err.response && err.response.status === 409) {
        errorMessage = err.response.data.error || 'Ya existe un curso con ese nombre';
      } else if (err.response && err.response.data && err.response.data.errors) {
        const serverErrors = {};
        err.response.data.errors.forEach(error => {
          serverErrors[error.param] = error.msg;
        });
        setFormErrors(serverErrors);
        return;
      }
      
      await alertService.error('Error', errorMessage);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasChanges()) {
        const message = 'Tienes cambios sin guardar. ¿Estás seguro de salir?';
        event.returnValue = message;
        return message;
      }
    };

    const handleHistoryPopState = (event) => {
      if (hasChanges()) {
        const message = 'Tienes cambios sin guardar. ¿Estás seguro de salir?';
        if (window.confirm(message)) {
          navigate('/cursos');
        } else {
          event.preventDefault();
        }
      } else {
        navigate('/cursos');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleHistoryPopState);
    window.history.pushState(null, null, window.location.href);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleHistoryPopState);
    };
  }, [hasChanges, navigate]);
    
  return (
    <div className="dashboard-container">
      {(!isMobile || (isMobile && sidebarOpen)) && (
        <Sidebar
          onLogoClick={() => {
            if (isMobile) setSidebarOpen(false);
          }}
          onLinkClick={handleCloseSidebar}
        />
      )}
      <div className={`main-content ${isFilterOpen ? 'panel-open' : ''}`} onClick={handleCloseSidebar}>
        <div className="dashboard-title-card">
          {isMobile && (
            <button 
              className="menu-toggle"
              onClick={toggleSidebar}
              aria-label="Abrir menú"
            >
              <i className="fas fa-bars"></i>
            </button>
          )}
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">
              {isEditing ? 'Editar Curso' : 'Crear Curso'}
            </h1>
            <span className="dashboard-title-sub">
              {isEditing ? 'Modificar información del curso' : 'Registrar nuevo curso'}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

        <div className="futuristic-form-container">
          <div className="form-header">
            <h2 className="form-title">
              <i className="fas fa-book"></i>
              Información del Curso
            </h2>
            <button className="back-button" onClick={handleBackButtonClick}>
              <i className="fas fa-arrow-left"></i>
              Regresar
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="two-column-form">
              <div className="form-column">
                <div className="form-group">
                  <label>ID Curso</label>
                  <input
                    type="text"
                    name="id_curso"
                    value={formData.id_curso}  
                    disabled
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Código del Curso</label>
                  <input
                    type="text"
                    name="codigo_curso"
                    value={formData.codigo_curso} 
                    disabled
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-column">
                <div className="form-group">
                  <label>Nombre del Curso</label>
                  <input
                    type="text"
                    name="nombre_curso"
                    value={formData.nombre_curso}
                    onChange={handleChange}
                    maxLength="50"
                    placeholder="Ingresa el nombre del curso"
                    className={`form-input ${formErrors.nombre_curso ? 'error' : ''}`}
                  />
                  {formErrors.nombre_curso && (
                    <span className="error-message">{formErrors.nombre_curso}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="submit-button-container">
              <button 
                type="submit" 
                className="submit-button"
                disabled={!isFormChanged}
              >
                <i className="fas fa-save"></i>
                {isEditing ? 'Guardar Cambios' : 'Crear Curso'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {isMobile && sidebarOpen && (
        <div className="overlay" onClick={handleCloseSidebar}></div>
      )}
    </div>
  );
}

export default CourseForm;
